/**
 * Nolana theme — hero slideshow behaviour.
 * Ported from tema-demo/assets/js/nolana.js "Hero slider".
 *
 * - Shows one [data-hero-slide] at a time; dots ([data-hero-dot]) switch slides.
 * - Autoplay (data-autoplay / data-interval) pauses on hover and focus, and is disabled
 *   when the user prefers reduced motion or there is a single slide.
 * - Left/Right arrow keys on the dots move between slides.
 * - Theme Editor: selecting a slide block shows that slide.
 * Loaded from the section; guarded so editor reloads never double-bind.
 */
(function () {
  "use strict";

  if (document.documentElement.dataset.nolanaHeroReady) return;
  document.documentElement.dataset.nolanaHeroReady = "true";

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function setupHero(hero) {
    if (!hero || hero.dataset.heroBound === "true") return;
    hero.dataset.heroBound = "true";

    var slides = Array.prototype.slice.call(hero.querySelectorAll("[data-hero-slide]"));
    var dots = Array.prototype.slice.call(hero.querySelectorAll("[data-hero-dot]"));
    var interval = parseInt(hero.getAttribute("data-interval"), 10) || 6500;
    var autoplay = hero.getAttribute("data-autoplay") === "true";
    var index = 0;
    var timer = null;
    var paused = false; // true while the pointer or keyboard focus is inside the hero
    var userPaused = false; // true after the visitor pressed the pause control
    var toggle = hero.querySelector("[data-hero-toggle]");

    if (!slides.length) return;

    function goTo(i) {
      index = (i + slides.length) % slides.length;
      slides.forEach(function (slide, k) {
        var active = k === index;
        slide.classList.toggle("is-active", active);
        slide.hidden = !active;
      });
      dots.forEach(function (dot, k) {
        var active = k === index;
        dot.classList.toggle("is-active", active);
        dot.setAttribute("aria-selected", active ? "true" : "false");
        dot.setAttribute("tabindex", active ? "0" : "-1");
      });
    }

    function stop() {
      clearInterval(timer);
      timer = null;
    }

    function start() {
      stop();
      if (paused || userPaused || !autoplay || reduceMotion || slides.length < 2) return;
      timer = setInterval(function () {
        goTo(index + 1);
      }, interval);
    }

    function pause() {
      paused = true;
      stop();
    }

    function resume() {
      paused = false;
      start();
    }

    /* Visible pause / play control (WCAG 2.2.2); the choice survives hover and focus changes. */
    function setUserPaused(value) {
      userPaused = value;
      if (toggle) {
        toggle.setAttribute("aria-pressed", value ? "true" : "false");
        toggle.setAttribute("aria-label", value ? toggle.getAttribute("data-label-play") : toggle.getAttribute("data-label-pause"));
        toggle.querySelectorAll("[data-icon-pause]").forEach(function (el) { el.hidden = value; });
        toggle.querySelectorAll("[data-icon-play]").forEach(function (el) { el.hidden = !value; });
      }
      if (value) stop();
      else start();
    }
    if (toggle) {
      toggle.addEventListener("click", function () {
        setUserPaused(!userPaused);
      });
      if (reduceMotion || slides.length < 2) toggle.hidden = true;
    }

    dots.forEach(function (dot, k) {
      dot.addEventListener("click", function () {
        goTo(k);
        start();
      });
      dot.addEventListener("keydown", function (event) {
        if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
          event.preventDefault();
          goTo(index + (event.key === "ArrowRight" ? 1 : -1));
          if (dots[index]) dots[index].focus();
          start();
        }
      });
    });

    hero.addEventListener("mouseenter", pause);
    hero.addEventListener("mouseleave", resume);
    hero.addEventListener("focusin", pause);
    hero.addEventListener("focusout", function () {
      setTimeout(function () {
        if (!hero.contains(document.activeElement)) resume();
      }, 0);
    });

    hero.nolanaHero = { goTo: goTo, start: start, stop: stop, slides: slides };
    goTo(0);
    start();
  }

  function init(root) {
    (root || document).querySelectorAll("[data-hero]").forEach(setupHero);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    init(event.target);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    if (!event.target.querySelectorAll) return;
    event.target.querySelectorAll("[data-hero]").forEach(function (hero) {
      if (hero.nolanaHero) hero.nolanaHero.stop();
    });
  });

  document.addEventListener("shopify:block:select", function (event) {
    var slide = event.target.closest ? event.target.closest("[data-hero-slide]") : null;
    if (!slide) return;
    var hero = slide.closest("[data-hero]");
    if (!hero || !hero.nolanaHero) return;
    hero.nolanaHero.stop();
    hero.nolanaHero.goTo(hero.nolanaHero.slides.indexOf(slide));
  });

  document.addEventListener("shopify:block:deselect", function (event) {
    var slide = event.target.closest ? event.target.closest("[data-hero-slide]") : null;
    if (!slide) return;
    var hero = slide.closest("[data-hero]");
    if (hero && hero.nolanaHero) hero.nolanaHero.start();
  });
})();
